// Klaviyo onsite tracking: identifies shoppers and sends cart events for the abandoned-cart flow.
//
// Klaviyo sets a tracking cookie (__kla_id), which UK PECR only allows after the visitor agrees,
// so nothing loads and nothing is sent until they accept cookies in the banner.
//
// Build-time settings (both are public values, safe in the browser bundle):
//   REACT_APP_KLAVIYO_PUBLIC_KEY  Klaviyo > Settings > Account > API keys > Public API key (6 characters)
//   REACT_APP_KLAVIYO_LIST_ID     Klaviyo > Audience > Lists & segments > your newsletter list > Settings > List ID
// Without the key the site runs untracked; without the list ID footer sign-ups only get the discount code.

const PUBLIC_KEY = (process.env.REACT_APP_KLAVIYO_PUBLIC_KEY || "").trim();
const LIST_ID = (process.env.REACT_APP_KLAVIYO_LIST_ID || "").trim();
const API_REVISION = "2026-10-15";
const SITE_ORIGIN = "https://krunchmate.com";

// Without a key nothing optional is ever set, so there is nothing to ask consent for
export const KLAVIYO_ENABLED = Boolean(PUBLIC_KEY);

export const CONSENT_COOKIE = "km_cookie_consent";
const OPEN_SETTINGS_EVENT = "km-open-cookie-settings";
const SIX_MONTHS_SECONDS = 15552000;
const EMAIL_PATTERN = /^[^\s@<>]{1,64}@[^\s@<>]{1,190}\.[^\s@<>]{2,}$/;

let scriptRequested = false;
let lastIdentifiedEmail = "";

export function getConsent() {
  try {
    const match = document.cookie.split("; ").find((part) => part.startsWith(`${CONSENT_COOKIE}=`));
    const value = match ? match.slice(CONSENT_COOKIE.length + 1) : "";
    return value === "accepted" || value === "rejected" ? value : "";
  } catch {
    return "";
  }
}

export function setConsent(value) {
  const withdrawing = value === "rejected" && getConsent() === "accepted";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  try {
    document.cookie = `${CONSENT_COOKIE}=${value}; max-age=${SIX_MONTHS_SECONDS}; path=/; SameSite=Lax${secure}`;
  } catch {
    // cookies blocked: the choice applies to this page view only
  }
  if (value === "accepted") initKlaviyo();
  if (withdrawing) {
    // Remove Klaviyo's cookie and reload, so the script already running on this page stops too
    try {
      document.cookie = "__kla_id=; max-age=0; path=/";
      document.cookie = `__kla_id=; max-age=0; path=/; domain=.${window.location.hostname.replace(/^www\./, "")}`;
    } catch {
      // cookies blocked: nothing was stored
    }
    window.location.reload();
  }
}

// The footer's "Cookie settings" link reopens the banner so a choice can be changed
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

export function onOpenCookieSettings(callback) {
  window.addEventListener(OPEN_SETTINGS_EVENT, callback);
  return () => window.removeEventListener(OPEN_SETTINGS_EVENT, callback);
}

const isActive = () => Boolean(PUBLIC_KEY) && getConsent() === "accepted";

// Loads klaviyo.js once; calls queued on window._learnq before it arrives are replayed by Klaviyo
export function initKlaviyo() {
  if (!isActive() || scriptRequested) return;
  scriptRequested = true;
  window._learnq = window._learnq || [];
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${encodeURIComponent(PUBLIC_KEY)}`;
  document.head.appendChild(script);
}

function push(command) {
  if (!isActive()) return;
  initKlaviyo();
  window._learnq.push(command);
}

export function identify(email) {
  const clean = typeof email === "string" ? email.trim().toLowerCase() : "";
  if (!EMAIL_PATTERN.test(clean) || clean === lastIdentifiedEmail || !isActive()) return;
  lastIdentifiedEmail = clean;
  push(["identify", { $email: clean }]);
}

const absoluteUrl = (path) => (path ? `${SITE_ORIGIN}${path}` : undefined);
const roundPounds = (value) => Math.round(value * 100) / 100;

// One line of the cart in the shape Klaviyo's abandoned-cart templates read
function toKlaviyoItem(line) {
  return {
    ProductID: line.wooId,
    SKU: line.id,
    ProductName: line.flavour?.name || line.id,
    Quantity: line.qty,
    ItemPrice: roundPounds(line.unitPrice),
    RowTotal: roundPounds(line.lineTotal),
    ProductURL: absoluteUrl("/shop"),
    ImageURL: absoluteUrl(line.flavour?.front),
  };
}

function cartProperties(detailed, subtotal) {
  const items = detailed.map(toKlaviyoItem);
  return {
    $value: roundPounds(subtotal),
    ItemNames: items.map((item) => item.ProductName),
    CheckoutURL: absoluteUrl("/checkout"),
    Items: items,
  };
}

export function trackAddedToCart(addedLine, detailed, subtotal) {
  const added = toKlaviyoItem(addedLine);
  push([
    "track",
    "Added to Cart",
    {
      ...cartProperties(detailed, subtotal),
      AddedItemProductName: added.ProductName,
      AddedItemProductID: added.ProductID,
      AddedItemSKU: added.SKU,
      AddedItemPrice: added.ItemPrice,
      AddedItemQuantity: addedLine.addedQty,
      AddedItemImageURL: added.ImageURL,
      AddedItemURL: added.ProductURL,
    },
  ]);
}

export function trackStartedCheckout(detailed, subtotal) {
  push([
    "track",
    "Started Checkout",
    {
      // Same basket, same event ID: Klaviyo counts a re-visit to checkout once, not twice
      $event_id: `${detailed.map((line) => `${line.id}x${line.qty}`).join("-")}-${Math.floor(Date.now() / 3600000)}`,
      ...cartProperties(detailed, subtotal),
    },
  ]);
}

// Adds someone who asked for marketing email to the newsletter list. It's their own form submission
// rather than a tracking cookie, so it doesn't wait for cookie consent. Returns whether Klaviyo accepted it.
export async function subscribeToNewsletter(email, source) {
  const clean = typeof email === "string" ? email.trim().toLowerCase() : "";
  if (!PUBLIC_KEY || !LIST_ID || !EMAIL_PATTERN.test(clean)) return false;
  try {
    const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(PUBLIC_KEY)}`, {
      method: "POST",
      headers: { "Content-Type": "application/vnd.api+json", revision: API_REVISION },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            custom_source: source,
            profile: {
              data: {
                type: "profile",
                attributes: { email: clean, subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } } },
              },
            },
          },
          relationships: { list: { data: { type: "list", id: LIST_ID } } },
        },
      }),
    });
    return response.status === 202;
  } catch {
    return false;
  }
}

// Answer to "What stopped you from krunching today?", so Klaviyo can tailor the abandoned-cart email
export function trackCartFeedback(reason, subtotal) {
  push(["track", "Cart Feedback", { Reason: reason, $value: roundPounds(subtotal) }]);
}
